/**
 * The token economics charts (CostsPage.tsx), drawn by hand so they take the console's tokens in light and dark, as the
 * team Overview's charts do: the trend over time (lines with a crosshair and the bucket's figures on hover), an
 * allocation (a donut with its legend, every slice named with its figure and share), and a ranking (bars of what each
 * thing cost, each row opening the thing). Colour is never the only signal: every mark has its number in words beside
 * it or under the pointer, and the series colours follow the entity, never its rank. Styles in economics.css.
 */

import type { CostGrouping, CostPoint, CostRank } from '@aioe/shared';
import { dateMedium } from '@aioe/shared/wording/dateTime';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { MemberFace } from '../teams/MemberFace.tsx';
import { compact, rankHref, valueOf, type Measure } from './costModel.ts';
import './economics.css';

/** The categorical slots, in order; "everything else" is grey. */
const SERIES = ['var(--series-1)', 'var(--series-2)', 'var(--series-3)', 'var(--series-4)', 'var(--series-5)'];
export const seriesColour = (i: number, rest?: boolean) => (rest ? 'var(--series-rest)' : SERIES[i % SERIES.length]!);

export interface TrendSeries<P = CostPoint> {
  label: string;
  colour: string;
  value: (p: P) => number;
}

/** A tidy top for an axis: 1, 2, 2.5 or 5 times a power of ten at or above the largest value. */
function niceTop(max: number): number {
  if (max <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(max));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= max) return m * p;
  return 10 * p;
}

/** Lines over the period, one per series, on one axis; the bucket under the pointer shows its figures. */
export function TrendChart<P extends { at: string } = CostPoint>({ points, series, format, label, bucket }: { points: P[]; series: TrendSeries<P>[]; format: (n: number) => string; label: string; bucket: string }) {
  const [hover, setHover] = useState<number>();
  if (points.length < 2) return <p className="econ__note">Not enough of the period has passed to draw a line.</p>;
  const W = 600;
  const H = 200;
  const max = niceTop(Math.max(0, ...points.flatMap((p) => series.map((s) => s.value(p)))));
  const x = (i: number) => (i / (points.length - 1)) * W;
  const y = (v: number) => H - (v / max) * H;
  const path = (s: TrendSeries<P>) => points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(s.value(p)).toFixed(1)}`).join(' ');
  const wash = (s: TrendSeries<P>) => `${path(s)} L${W},${H} L0,${H} Z`;
  const day = (at: string) => dateMedium(at, { year: false, timeZone: 'UTC' });
  const labelAt = [0, Math.round((points.length - 1) / 2), points.length - 1];
  const totals = series.map((s) => points.reduce((n, p) => n + s.value(p), 0));
  const p = hover !== undefined ? points[hover] : undefined;
  const summary = series.map((s, i) => `${s.label} ${format(totals[i]!)}`).join(', ');
  return (
    <figure className="econ-trend" aria-label={`${label} per ${bucket}, ${day(points[0]!.at)} to ${day(points.at(-1)!.at)}: ${summary}.`}>
      {series.length > 1 ? (
        <div className="econ-legend" aria-hidden="true">
          {series.map((s, i) => (
            <span key={s.label} style={{ ['--key' as string]: s.colour }}>
              <i className="econ-key" />
              {s.label} <b>{format(totals[i]!)}</b>
            </span>
          ))}
        </div>
      ) : null}
      <div className="econ-trend__plot">
        <div className="econ-trend__y" aria-hidden="true">
          {[1, 0.75, 0.5, 0.25, 0].map((f) => (
            <span key={f}>{format(max * f)}</span>
          ))}
        </div>
        <div className="econ-trend__area" onMouseLeave={() => setHover(undefined)}>
          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="econ-trend__svg" aria-hidden="true">
            {[0, 0.25, 0.5, 0.75, 1].map((f) => (
              <line key={f} x1={0} x2={W} y1={y(max * f)} y2={y(max * f)} className="econ-trend__grid" vectorEffect="non-scaling-stroke" />
            ))}
            {series.length === 1 ? <path d={wash(series[0]!)} className="econ-trend__wash" style={{ fill: series[0]!.colour }} /> : null}
            {series.map((s) => (
              <path key={s.label} d={path(s)} className="econ-trend__line" style={{ stroke: s.colour }} vectorEffect="non-scaling-stroke" />
            ))}
          </svg>
          {/* One hover target per bucket, wider than the line. */}
          <div className="econ-trend__hits">
            {points.map((pt, i) => (
              <span key={pt.at} onMouseEnter={() => setHover(i)} title={`${day(pt.at)}: ${series.map((s) => `${s.label} ${format(s.value(pt))}`).join(', ')}`} />
            ))}
          </div>
          {p && hover !== undefined ? (
            <>
              <span className="econ-trend__cross" style={{ left: `${(hover / (points.length - 1)) * 100}%` }} />
              {series.map((s) => (
                <span key={s.label} className="econ-trend__dot" style={{ left: `${(hover / (points.length - 1)) * 100}%`, top: `${(1 - s.value(p) / max) * 100}%`, background: s.colour }} />
              ))}
              <div className="econ-tip" style={hover > points.length / 2 ? { right: `${(1 - hover / (points.length - 1)) * 100 + 2}%` } : { left: `${(hover / (points.length - 1)) * 100 + 2}%` }}>
                <div className="econ-tip__head">{bucket === 'day' ? day(p.at) : `${bucket === 'week' ? 'Week of' : ''} ${day(p.at)}`.trim()}</div>
                {series.map((s) => (
                  <div key={s.label} className="econ-tip__row">
                    <span>{s.label}</span>
                    <b>{format(s.value(p))}</b>
                  </div>
                ))}
                {'calls' in p && typeof p.calls === 'number' ? (
                  <div className="econ-tip__row">
                    <span>Calls</span>
                    <b>{p.calls.toLocaleString('en-AU')}</b>
                  </div>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      </div>
      <div className="econ-trend__x" aria-hidden="true">
        {labelAt.map((i) => (
          <span key={i}>{day(points[i]!.at)}</span>
        ))}
      </div>
    </figure>
  );
}

/** A part-to-whole: the slices in order with a surface gap between them, and the legend naming each one's figure and share. */
export function Donut({ slices, by, measure, format, title, total, none = 'Not recorded' }: { slices: CostRank[]; by: CostGrouping | 'work'; measure: Measure; format: (n: number) => string; title: string; total: number; none?: string }) {
  const shown = slices.filter((s) => valueOf(s, measure) > 0);
  const sum = shown.reduce((n, s) => n + valueOf(s, measure), 0);
  const R = 42;
  const C = 2 * Math.PI * R;
  const gap = shown.length > 1 ? 1.6 : 0;
  let at = 0;
  const share = (v: number) => (total > 0 ? Math.round((v / total) * 1000) / 10 : 0);
  const named = (s: CostRank) => s.name ?? (s.key || none);
  let slot = 0;
  const colours = new Map(slices.map((s) => [s.key + (s.rest ? '\u0000rest' : ''), s.rest ? seriesColour(0, true) : seriesColour(slot++)] as const));
  const colourOf = (s: CostRank) => colours.get(s.key + (s.rest ? '\u0000rest' : ''))!;
  return (
    <figure className="econ-donut" aria-label={`${title}: ${shown.map((s) => `${named(s)} ${format(valueOf(s, measure))} (${share(valueOf(s, measure))}%)`).join(', ') || 'nothing in this period'}.`}>
      <div className="econ-donut__ring">
        <svg viewBox="0 0 100 100" aria-hidden="true">
          <circle cx={50} cy={50} r={R} className="econ-donut__track" />
          {sum > 0
            ? shown.map((s) => {
                const len = (valueOf(s, measure) / sum) * C;
                const dash = Math.max(0.01, len - gap);
                const el = <circle key={s.key + (s.rest ? 'r' : '')} cx={50} cy={50} r={R} className="econ-donut__slice" style={{ stroke: colourOf(s) }} strokeDasharray={`${dash} ${C - dash}`} strokeDashoffset={-at} />;
                at += len;
                return el;
              })
            : null}
        </svg>
        <div className="econ-donut__centre" aria-hidden="true">
          <span className="econ-donut__total">{format(total)}</span>
          <span className="econ-donut__unit">{measure === 'usd' ? 'spend' : 'tokens'}</span>
        </div>
      </div>
      <ul className="econ-slices">
        {slices.length === 0 ? <li className="econ__note">Nothing in this period.</li> : null}
        {slices.map((s) => {
          const href = rankHref(by, s);
          return (
            <li key={s.key + (s.rest ? 'r' : '')} style={{ ['--key' as string]: colourOf(s) }}>
              <span className="econ-swatch" aria-hidden="true" />
              <span className={`econ-slices__name ${s.rest || !s.key ? 'text-muted' : ''}`} title={named(s)}>
                {href ? <Link to={href}>{named(s)}</Link> : named(s)}
              </span>
              <span className="econ-slices__value">
                {format(valueOf(s, measure))} · {share(valueOf(s, measure))}%
              </span>
            </li>
          );
        })}
      </ul>
    </figure>
  );
}

/** Things ranked by what they cost, the bar against the first; each row opens the thing. `name` renders a row's words. */
export function RankBars({ ranks, by, measure, format, name, empty }: { ranks: CostRank[]; by: CostGrouping; measure: Measure; format: (n: number) => string; name?: (r: CostRank) => ReactNode; empty: ReactNode }) {
  if (!ranks.length) return <p className="econ__note">{empty}</p>;
  const max = Math.max(...ranks.map((r) => valueOf(r, measure)), 0) || 1;
  return (
    <ol className="econ-rank">
      {ranks.map((r) => {
        const href = rankHref(by, r);
        const v = valueOf(r, measure);
        const second = measure === 'usd' && r.subscriptionUsd > 0 ? ` · ${format(r.subscriptionUsd)} on paid seats` : '';
        const inner = (
          <>
            <span className="econ-rank__name">
              {r.face ? <MemberFace member={{ ...r.face, human: r.face.human }} /> : null}
              <span className="econ-rank__words">
                {name ? name(r) : (r.name ?? r.key)}
                {r.detail ? <span className="econ-rank__detail"> · {r.detail}</span> : null}
              </span>
            </span>
            <span className="econ-rank__value">{format(v)}</span>
            <span className="econ-rank__track" aria-hidden="true">
              <span className="econ-rank__bar" style={{ width: `${Math.max(v > 0 ? 2 : 0, (v / max) * 100)}%` }} />
            </span>
          </>
        );
        const title = `${r.name ?? r.key}: ${format(v)}${second}, ${compact(r.tokens)} tokens, ${r.calls.toLocaleString('en-AU')} calls`;
        return (
          <li key={r.key}>
            {href ? (
              <Link to={href} className="econ-rank__row" title={title}>
                {inner}
              </Link>
            ) : (
              <div className="econ-rank__row" title={title}>
                {inner}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/** A line's shape over the period, for a breakdown row. */
export function Sparkline({ points, measure }: { points: CostPoint[]; measure: Measure }) {
  const vals = points.map((p) => valueOf(p, measure));
  const max = Math.max(0, ...vals);
  if (points.length < 2 || max === 0) return null;
  const d = vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${((i / (vals.length - 1)) * 100).toFixed(1)},${(10 - (v / max) * 9).toFixed(2)}`).join(' ');
  return (
    <svg viewBox="0 0 100 10" preserveAspectRatio="none" className="econ-spark" aria-hidden="true">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
